'use client';

import { useEffect, useState } from 'react';
import { Toaster, type ToasterProps } from 'sonner';
import { useDarkMode } from '@/lib/appearance/dark-mode-context';

type ThemedToasterProps = Omit<ToasterProps, 'theme'>;

/** Sonner switches to full-width mobile layout at 600px. */
const SONNER_MOBILE_MAX_WIDTH_PX = 600;

const defaultMobileOffset: ToasterProps['mobileOffset'] = {
  bottom: 'max(1rem, env(safe-area-inset-bottom))',
  left: '1rem',
  right: '1rem',
};

function useSonnerMobileLayout() {
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const mq = window.matchMedia(`(max-width: ${SONNER_MOBILE_MAX_WIDTH_PX}px)`);
    const sync = () => setIsMobile(mq.matches);
    sync();
    mq.addEventListener('change', sync);
    return () => mq.removeEventListener('change', sync);
  }, []);

  return isMobile;
}

/** Sonner toaster that follows the app dark mode preference. */
export function ThemedToaster({
  position = 'top-center',
  mobileOffset = defaultMobileOffset,
  offset,
  style,
  className,
  ...props
}: ThemedToasterProps) {
  const { darkMode } = useDarkMode();
  const isMobile = useSonnerMobileLayout();

  // Mobile: bottom-center keeps feedback near the thumb and clear of top chrome / hero copy.
  const resolvedPosition =
    position === 'top-center' && isMobile ? 'bottom-center' : position;

  return (
    <Toaster
      theme={darkMode ? 'dark' : 'light'}
      position={resolvedPosition}
      offset={offset ?? (resolvedPosition.startsWith('top') ? 16 : 20)}
      mobileOffset={mobileOffset}
      className={['!z-[400]', className].filter(Boolean).join(' ')}
      style={{ ...style, zIndex: 400 }}
      {...props}
    />
  );
}
